/**
 * Hook to load profile and contacts data.
 * Uses async GraphQL calls when VITE_MOCK_DATA=false,
 * falls back to sync mock data otherwise.
 */

import { useState, useEffect } from 'react';
import {
  getStudent,
  getEntity,
  getContactsForStudent,
  fetchUserProfile,
  fetchContacts,
  type Student,
  type Entity,
  type Contact,
} from '@/lib/data';

const isMockData = import.meta.env.VITE_MOCK_DATA === 'true';

interface ProfileData {
  student: Student | undefined;
  entity: Entity | undefined;
  contacts: Contact[];
  loading: boolean;
  error: string | null;
  refetchContacts: () => void;
}

export function useProfileData(
  paramStudentId: string | undefined,
  paramEntityId: string | undefined,
  claimsStudentId: string | undefined,
): ProfileData {
  const [student, setStudent] = useState<Student | undefined>(undefined);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(!isMockData);
  const [error, setError] = useState<string | null>(null);

  // Entity is always from mock/config for now (no GraphQL query)
  const entity = paramEntityId ? getEntity(paramEntityId) : undefined;

  const resolvedId = paramStudentId || claimsStudentId || '';

  const loadData = async () => {
    if (isMockData) {
      setStudent(paramStudentId ? getStudent(paramStudentId) : undefined);
      setContacts(getContactsForStudent(resolvedId));
      setLoading(false);
      return;
    }

    if (!resolvedId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [profileResult, contactsResult] = await Promise.all([
        fetchUserProfile(resolvedId),
        fetchContacts(resolvedId),
      ]);
      setStudent(profileResult);
      setContacts(contactsResult);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load profile data';
      setError(msg);
      console.error('[useProfileData]', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [resolvedId, paramStudentId]);

  const refetchContacts = async () => {
    if (isMockData) {
      setContacts(getContactsForStudent(resolvedId));
      return;
    }
    if (!resolvedId) return;
    try {
      const result = await fetchContacts(resolvedId);
      setContacts(result);
    } catch (err) {
      console.error('[useProfileData] refetch failed', err);
    }
  };

  return { student, entity, contacts, loading, error, refetchContacts };
}
